import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const CartDrawer = ({ isOpen, onClose, cart, updateQuantity, removeFromCart, clearCart }) => {
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);

  const subtotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  const itemCount = cart.reduce((total, item) => total + item.quantity, 0);

  // Reset states when drawer opens/closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setIsCheckingOut(false);
        setCheckoutSuccess(false);
      }, 500);
    }
  }, [isOpen]);

  const handleCheckout = () => {
    setIsCheckingOut(true);
    // Simulate network request
    setTimeout(() => {
      setIsCheckingOut(false);
      setCheckoutSuccess(true);
      clearCart();
    }, 2000);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 z-[60] backdrop-blur-sm"
            aria-hidden="true"
          />
          
          {/* Drawer */}
          <motion.div 
            role="dialog"
            aria-modal="true"
            aria-labelledby="cart-title"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            className="fixed top-0 right-0 h-[100svh] w-full max-w-md bg-[#F9F8F6] z-[70] shadow-2xl flex flex-col"
          >
            <div className="p-6 md:p-8 flex justify-between items-center border-b border-[#E5E5E5] bg-white shrink-0">
              <h2 id="cart-title" className="font-serif text-2xl text-[#2C2C2C]">
                {checkoutSuccess ? 'Order Complete' : `Your Cart (${itemCount})`}
              </h2>
              <button 
                onClick={onClose} 
                aria-label="Close cart"
                className="p-2 hover:bg-black/5 rounded-full transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355]"
              >
                <SafeIcon icon={FiIcons.FiX} className="w-6 h-6 text-[#737373] group-hover:text-[#2C2C2C] transition-colors" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto relative">
              {checkoutSuccess ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center bg-[#F9F8F6]">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", bounce: 0.5 }}
                    className="w-16 h-16 bg-[#8B7355] rounded-full flex items-center justify-center mb-6"
                  >
                    <SafeIcon icon={FiIcons.FiCheck} className="w-8 h-8 text-white" />
                  </motion.div>
                  <h3 className="text-2xl font-serif text-[#2C2C2C] mb-3">Thank you for your order</h3>
                  <p className="text-[#737373] mb-8 leading-relaxed">
                    We've received your order and will begin processing it shortly. A confirmation email has been sent to you.
                  </p>
                  <button 
                    onClick={onClose}
                    className="w-full py-4 bg-[#2C2C2C] text-white tracking-[0.15em] text-sm uppercase hover:bg-black transition-colors font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] focus-visible:ring-offset-2"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : cart.length === 0 ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
                  <SafeIcon icon={FiIcons.FiShoppingBag} className="w-16 h-16 text-[#E5E5E5] mb-6" />
                  <h3 className="text-xl font-serif text-[#2C2C2C] mb-2">Your cart is empty</h3>
                  <p className="text-[#737373] text-sm mb-8">Looks like you haven't added anything to your space yet.</p>
                  <button 
                    onClick={onClose}
                    className="border border-[#2C2C2C] text-[#2C2C2C] px-8 py-4 text-xs tracking-[0.15em] uppercase hover:bg-[#2C2C2C] hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] focus-visible:ring-offset-2"
                  >
                    Explore Collections
                  </button>
                </div>
              ) : (
                <div className="p-6 md:p-8 space-y-8">
                  <AnimatePresence initial={false}>
                    {cart.map((item) => (
                      <motion.div 
                        key={item.id}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="flex gap-6 group"
                      >
                        <div className="w-24 h-32 bg-[#EFECE8] overflow-hidden rounded-sm shrink-0">
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        </div>
                        <div className="flex-1 flex flex-col justify-between py-1">
                          <div>
                            <div className="flex justify-between items-start gap-2 mb-1">
                              <h3 className="text-base font-medium text-[#2C2C2C] leading-tight pr-4">{item.name}</h3>
                              <p className="text-base text-[#2C2C2C] font-medium whitespace-nowrap">${item.price}</p>
                            </div>
                            <p className="text-sm text-[#737373]">{item.tag || 'Standard'}</p>
                          </div>
                          <div className="flex justify-between items-end mt-4">
                            <div className="flex items-center border border-[#E5E5E5] rounded-sm bg-white">
                              <button 
                                aria-label="Decrease quantity"
                                onClick={() => updateQuantity(item.id, -1)}
                                className="px-3 py-1.5 text-sm text-[#737373] hover:text-[#2C2C2C] transition-colors focus-visible:outline-none focus-visible:bg-[#F9F8F6]"
                              >
                                -
                              </button>
                              <span className="px-3 text-sm font-medium w-8 text-center">{item.quantity}</span>
                              <button 
                                aria-label="Increase quantity"
                                onClick={() => updateQuantity(item.id, 1)}
                                className="px-3 py-1.5 text-sm text-[#737373] hover:text-[#2C2C2C] transition-colors focus-visible:outline-none focus-visible:bg-[#F9F8F6]"
                              >
                                +
                              </button>
                            </div>
                            <button 
                              onClick={() => removeFromCart(item.id)}
                              aria-label={`Remove ${item.name} from cart`}
                              className="text-xs text-[#737373] hover:text-[#2C2C2C] underline underline-offset-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] rounded-sm"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {!checkoutSuccess && cart.length > 0 && (
              <div className="p-6 md:p-8 border-t border-[#E5E5E5] bg-white shadow-[0_-4px_20px_rgba(0,0,0,0.02)] shrink-0">
                <div className="flex justify-between mb-2 text-[#2C2C2C] text-lg">
                  <span className="font-medium">Subtotal</span>
                  <span className="font-medium">${subtotal.toLocaleString()}</span>
                </div>
                <p className="text-sm text-[#737373] mb-6">Shipping and taxes calculated at checkout.</p>
                <button 
                  onClick={handleCheckout}
                  disabled={isCheckingOut}
                  className="w-full py-4 bg-[#2C2C2C] text-white tracking-[0.15em] text-sm uppercase hover:bg-black transition-colors font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] focus-visible:ring-offset-2 disabled:bg-[#737373] disabled:cursor-not-allowed flex justify-center items-center h-14"
                >
                  {isCheckingOut ? (
                    <motion.div 
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                      className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                    />
                  ) : (
                    'Checkout'
                  )}
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;